import { formatCnpj, formatCpf, fmtTelefone } from "@/utils/fmt";
import { Icon } from "@/components/common/Icon";
import { Checkbox, Field } from "@/components/onboarding/OnbFields";
import { WsVerifyField } from "./WsVerifyField";

import type { OrgForm, OrgSetField } from "./types";

interface Props {
  form: OrgForm;
  setField: OrgSetField;
  setDocType: (t: "cnpj" | "cpf") => void;
  lookup: () => void | Promise<void>;
  isLooking: boolean;
  workspaceName: string;
  // Quando true, exibe form de cadastro completo (user novo).
  needsAuth?: boolean;
  emailMasked?: string;
}

const UFS = [
  "SP", "RJ", "MG", "RS", "SC", "PR", "BA", "CE",
  "GO", "AM", "PE", "DF", "ES", "PA", "MA",
];

const REGIMES = [
  "Simples Nacional",
  "Lucro Presumido",
  "Lucro Real",
  "MEI",
  "Imune/Isenta",
];

export function OrgStepOrg({
  form,
  setField,
  setDocType,
  lookup,
  isLooking,
  workspaceName,
  needsAuth = false,
  emailMasked,
}: Props) {
  const isPj = form.docType === "cnpj";

  return (
    <>
      <div className="onb-section-head">
        <div className="onb-step-eyebrow">ETAPA 1 · OBRIGATÓRIA</div>
        <h1 className="onb-step-title">Cadastre sua organização</h1>
        <p className="onb-step-sub">
          Uma organização pode ser um <strong>grupo econômico</strong> ou{" "}
          <strong>holding</strong> (CNPJ) ou uma{" "}
          <strong>pessoa física</strong> (CPF). Você anexa os CNPJs/CPFs do
          grupo depois — não precisa cadastrar um a um.
        </p>
      </div>

      <div className="onb-section">
        <div className="onb-section-title">Tipo de cadastro</div>
        <p className="onb-section-sub">
          Escolha se a organização representa uma empresa (PJ) ou uma pessoa
          física (PF).
        </p>
        <div
          className="emp-doc-toggle emp-doc-toggle-lg"
          role="tablist"
          aria-label="Tipo de cadastro da organização"
        >
          <button
            type="button"
            role="tab"
            aria-selected={isPj}
            className={`emp-doc-tab ${isPj ? "is-on" : ""}`}
            onClick={() => setDocType("cnpj")}
          >
            <Icon name="building" size={13} /> CNPJ{" "}
            <span className="emp-doc-tab-tag">Pessoa Jurídica</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={!isPj}
            className={`emp-doc-tab ${!isPj ? "is-on" : ""}`}
            onClick={() => setDocType("cpf")}
          >
            <Icon name="user" size={13} /> CPF{" "}
            <span className="emp-doc-tab-tag">Pessoa Física</span>
          </button>
        </div>
      </div>

      <div className="onb-section">
        <div className="onb-section-title">Identificação</div>
        <div className="onb-field-grid onb-grid-2">
          <Field label="Nome da organização" required>
            <input
              className="onb-input"
              maxLength={120}
              placeholder={
                isPj ? "Ex: BRF Holdings" : "Ex: Escritório João Silva"
              }
              value={form.nomeOrg}
              onChange={(e) => setField("nomeOrg", e.target.value)}
            />
          </Field>
          {isPj ? (
            <Field
              label="CNPJ da controladora"
              required
              hint={
                isLooking
                  ? "consultando Receita Federal..."
                  : "razão, CNAE e regime são preenchidos automaticamente"
              }
            >
              <div className="onb-input-wrap">
                <Icon name="building" size={14} />
                <input
                  className="onb-input bare"
                  placeholder="00.000.000/0001-00"
                  inputMode="numeric"
                  maxLength={18}
                  value={form.cnpj}
                  onChange={(e) => setField("cnpj", formatCnpj(e.target.value))}
                  onBlur={lookup}
                />
              </div>
            </Field>
          ) : (
            <Field
              label="CPF"
              required
              hint={
                isLooking
                  ? "consultando Receita Federal..."
                  : "nome e endereço são preenchidos automaticamente"
              }
            >
              <div className="onb-input-wrap">
                <Icon name="user" size={14} />
                <input
                  className="onb-input bare"
                  placeholder="000.000.000-00"
                  inputMode="numeric"
                  maxLength={14}
                  value={form.cpf}
                  onChange={(e) => setField("cpf", formatCpf(e.target.value))}
                  onBlur={lookup}
                />
              </div>
            </Field>
          )}
        </div>
      </div>

      <div className="onb-section">
        <div className="onb-section-title">
          Dados cadastrais (auto-preenchidos)
        </div>
        {isPj ? (
          <>
            <div className="onb-field-grid onb-grid-2">
              <Field label="Razão social" required>
                <input
                  className="onb-input"
                  maxLength={200}
                  value={form.razao}
                  onChange={(e) => setField("razao", e.target.value)}
                  placeholder="—"
                />
              </Field>
              <Field label="Nome fantasia">
                <input
                  className="onb-input"
                  maxLength={100}
                  value={form.fantasia}
                  onChange={(e) => setField("fantasia", e.target.value)}
                  placeholder="—"
                />
              </Field>
            </div>
            <div className="onb-field-grid onb-grid-3-1">
              <Field label="Cidade">
                <input
                  className="onb-input"
                  maxLength={80}
                  value={form.cidade}
                  onChange={(e) => setField("cidade", e.target.value)}
                  placeholder="—"
                />
              </Field>
              <Field label="UF">
                <select
                  className="onb-input"
                  value={form.sede}
                  onChange={(e) => setField("sede", e.target.value)}
                >
                  {UFS.map((uf) => (
                    <option key={uf}>{uf}</option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="onb-field-grid onb-grid-2">
              <Field label="CNAE principal" hint="lido da Receita Federal (não editável)">
                <input
                  className="onb-input"
                  value={form.cnae}
                  disabled
                  readOnly
                  placeholder="—"
                />
              </Field>
              <Field label="Regime tributário" hint="lido da Receita Federal (não editável)">
                <select className="onb-input" value={form.regime || ""} disabled>
                  <option value="">—</option>
                  {REGIMES.map((r) => (
                    <option key={r}>{r}</option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="onb-section-title" style={{ marginTop: 16 }}>
              Endereço
            </div>
            <div className="onb-field-grid onb-grid-3-1">
              <Field label="Logradouro">
                <input
                  className="onb-input"
                  maxLength={255}
                  value={form.logradouro}
                  onChange={(e) => setField("logradouro", e.target.value)}
                  placeholder="—"
                />
              </Field>
              <Field label="Número">
                <input
                  className="onb-input"
                  maxLength={20}
                  value={form.numero}
                  onChange={(e) => setField("numero", e.target.value)}
                  placeholder="—"
                />
              </Field>
            </div>
            <div className="onb-field-grid onb-grid-2">
              <Field label="Complemento">
                <input
                  className="onb-input"
                  maxLength={100}
                  value={form.complemento}
                  onChange={(e) => setField("complemento", e.target.value)}
                  placeholder="—"
                />
              </Field>
              <Field label="Bairro">
                <input
                  className="onb-input"
                  maxLength={120}
                  value={form.bairro}
                  onChange={(e) => setField("bairro", e.target.value)}
                  placeholder="—"
                />
              </Field>
            </div>
            <div className="onb-field-grid onb-grid-2">
              <Field label="CEP">
                <input
                  className="onb-input"
                  maxLength={9}
                  value={form.cep}
                  onChange={(e) => setField("cep", e.target.value)}
                  placeholder="00000-000"
                />
              </Field>
            </div>
          </>
        ) : (
          <>
            <Field label="Nome completo" required>
              <input
                className="onb-input"
                maxLength={120}
                value={form.nomePf}
                onChange={(e) => setField("nomePf", e.target.value)}
                placeholder="—"
              />
            </Field>
            <div className="onb-field-grid onb-grid-3-1">
              <Field label="Cidade">
                <input
                  className="onb-input"
                  maxLength={80}
                  value={form.cidade}
                  onChange={(e) => setField("cidade", e.target.value)}
                  placeholder="—"
                />
              </Field>
              <Field label="UF">
                <select
                  className="onb-input"
                  value={form.sede}
                  onChange={(e) => setField("sede", e.target.value)}
                >
                  {UFS.map((uf) => (
                    <option key={uf}>{uf}</option>
                  ))}
                </select>
              </Field>
            </div>
          </>
        )}
      </div>

      <div className="onb-section">
        <div className="onb-section-title">Responsável legal da organização</div>
        <p className="onb-section-sub">
          CPF da pessoa que pode assinar pela organização. Pode ser o mesmo do
          workspace ou diferente.
        </p>

        <Field
          label="CPF do responsável"
          required
          hint={
            form.respUsarWs
              ? `usando o CPF do responsável legal do workspace ${workspaceName}`
              : "preencha o CPF do responsável desta organização"
          }
        >
          <input
            className="onb-input"
            inputMode="numeric"
            maxLength={14}
            placeholder="000.000.000-00"
            value={form.respUsarWs ? "••• mesmo do workspace" : form.respCpf}
            disabled={form.respUsarWs}
            onChange={(e) => setField("respCpf", formatCpf(e.target.value))}
          />
        </Field>

        <label className="onb-checkbox is-spaced">
          <span
            className={`onb-checkbox-box ${form.respUsarWs ? "is-on" : ""}`}
            onClick={() => setField("respUsarWs", !form.respUsarWs)}
          >
            {form.respUsarWs && <Icon name="check" size={12} stroke={3} />}
          </span>
          <span className="onb-checkbox-text">
            Usar o mesmo responsável legal do{" "}
            <strong className="onb-strong-fg1">
              workspace {workspaceName}
            </strong>
          </span>
        </label>
      </div>

      {needsAuth && (
        <div className="onb-section">
          <div className="onb-section-title">Usuário administrador (owner)</div>
          <p className="onb-section-sub">
            Você vai virar admin <strong>desta organização apenas</strong> —
            não verá outras organizações do workspace {workspaceName}. Defina
            seus dados de acesso.
          </p>

          <Field label="Nome completo" required>
            <input
              className="onb-input"
              maxLength={100}
              value={form.acceptOwnerNome}
              onChange={(e) => setField("acceptOwnerNome", e.target.value)}
              placeholder="João da Silva"
            />
          </Field>

          <WsVerifyField
            label="E-mail"
            channel="email"
            required
            placeholder={emailMasked ?? "voce@email.com.br"}
            type="email"
            icon="mail"
            inputMode="email"
            maxLength={120}
            value={form.acceptEmail}
            onChangeValue={(v) => {
              setField("acceptEmail", v);
              if (form.acceptEmailVerificado) setField("acceptEmailVerificado", false);
            }}
            isVerified={form.acceptEmailVerificado}
            onVerified={() => setField("acceptEmailVerificado", true)}
            hint="enviaremos um código de 6 dígitos · usado como login no TDAX"
          />

          <WsVerifyField
            label="Celular (WhatsApp/SMS)"
            channel="sms"
            required
            placeholder="(11) 90000-0000"
            type="tel"
            icon="phone"
            inputMode="tel"
            maxLength={15}
            formatter={fmtTelefone}
            value={form.acceptPhone}
            onChangeValue={(v) => {
              setField("acceptPhone", v);
              if (form.acceptPhoneVerificado) setField("acceptPhoneVerificado", false);
            }}
            isVerified={form.acceptPhoneVerificado}
            onVerified={() => setField("acceptPhoneVerificado", true)}
            hint="enviaremos um SMS com código de 6 dígitos · usado para 2FA"
          />

          <div className="onb-field-grid onb-grid-2">
            <Field
              label="Senha"
              required
              hint={
                form.acceptPassword && form.acceptPassword.length < 8
                  ? "mínimo 8 caracteres"
                  : "mínimo 8 caracteres"
              }
            >
              <input
                className="onb-input"
                type="password"
                minLength={8}
                maxLength={64}
                value={form.acceptPassword}
                onChange={(e) => setField("acceptPassword", e.target.value)}
                placeholder="••••••••"
              />
            </Field>
            <Field
              label="Confirmar senha"
              required
              hint={
                form.acceptPassword &&
                form.acceptPassword2 &&
                form.acceptPassword !== form.acceptPassword2
                  ? "as senhas não conferem"
                  : undefined
              }
            >
              <input
                className="onb-input"
                type="password"
                maxLength={64}
                value={form.acceptPassword2}
                onChange={(e) => setField("acceptPassword2", e.target.value)}
                placeholder="••••••••"
              />
            </Field>
          </div>
        </div>
      )}

      {!needsAuth && (
        <div className="onb-section">
          <div className="onb-section-title">Confirmar sua conta</div>
          <p className="onb-section-sub">
            Este e-mail{emailMasked ? ` (${emailMasked})` : ""} já tem uma conta no
            TDAX. Para vincular esta organização à sua conta, confirme sua senha.
          </p>
          <Field label="Senha da sua conta" required>
            <input
              className="onb-input"
              type="password"
              maxLength={64}
              value={form.existingAccountPassword}
              onChange={(e) => setField("existingAccountPassword", e.target.value)}
              placeholder="••••••••"
            />
          </Field>
        </div>
      )}

      <div className="onb-section onb-section-terms">
        {needsAuth && (
          <Checkbox
            checked={form.acceptAceiteTermos}
            onChange={(v) => setField("acceptAceiteTermos", v)}
          >
            Li e aceito os{" "}
            <a href="#" className="onb-link">
              Termos de uso
            </a>{" "}
            e a{" "}
            <a href="#" className="onb-link">
              Política de privacidade
            </a>{" "}
            do TDAX.
          </Checkbox>
        )}
        <Checkbox
          checked={form.aceiteLgpd}
          onChange={(v) => setField("aceiteLgpd", v)}
        >
          Autorizo o Tributo Devido a armazenar e processar os dados informados
          conforme a{" "}
          <a href="#" className="onb-link">
            LGPD (Lei 13.709/2018)
          </a>
          .
        </Checkbox>
      </div>
    </>
  );
}
